import { useBreakpointValue, useToast } from '@hover/blueprint';
import {
  iMinusCircle,
  iconEdit,
  iconScan,
  iconTrash,
  iconViewGridMedium,
} from '@hover/icons';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';

import { DisplayDestructiveConfirmation } from 'src/modules/homeownerApp/components/Projects/Menu/MoreActions/DisplayDestructiveConfirmation';
import type { MenuOption } from 'src/modules/homeownerApp/components/Projects/Menu/MoreActions/MoreActionMenu';
import { MoreActionMenu } from 'src/modules/homeownerApp/components/Projects/Menu/MoreActions/MoreActionMenu';
import { UpdateScanMenu } from 'src/modules/homeownerApp/components/Projects/Menu/MoreActions/UpdateScanMenu';
import { Actions } from 'src/modules/homeownerApp/components/Projects/Menu/MoreActions/types';
import { useAttachableScans } from 'src/modules/homeownerApp/components/Projects/Project/hooks';
import {
  useDeleteProjectClickHandler,
  useErrors,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { useRemoveScanFromProjectHandler } from 'src/modules/homeownerApp/hooks/useRemoveScanFromProjectHandler';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useGetProjectQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';

const ProjectMoreActions = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const history = useHistory();
  const toast = useToast();
  const { setCurrentMenu } = useHomeownerAppContext();
  const { showError } = useErrors();
  const attachableScans = useAttachableScans();
  const isMobile = useBreakpointValue({ base: true, tablet: false });

  const formatError = useCallback(
    (errorMessage: string): string =>
      t('projects.confirmation.delete.error', { errorMessage }),
    [t],
  );

  const [step, setStep] = useState<Actions>(Actions.MORE_ACTIONS);

  const { params } = useRouteMatch(Routes.HOMEOWNER_APP_PROJECT) ?? {
    params: {},
  };

  const { projectId } = params as { projectId: string };
  const { data } = useGetProjectQuery({
    variables: { projectId: Number(projectId) },
  });
  const attachedScan = data?.project?.jobs?.[0];

  const [deleteProjectClickHandler, deleteResult] =
    useDeleteProjectClickHandler(Number(projectId));
  const [removeScanHandler, removeScanResult] = useRemoveScanFromProjectHandler(
    Number(projectId),
    attachedScan,
  );

  const handleDeleteClick = () => {
    deleteProjectClickHandler({
      onSuccess: () => {
        setCurrentMenu(null);
        history.push(Routes.HOMEOWNER_APP_PROJECTS_LIST.path);
        toast({
          props: {
            message: t('customize.newProject.deleteProjectSuccess'),
          },
        });
      },
      onError: () => {
        setCurrentMenu(null);
        showError({
          error: deleteResult.error as Error,
          formatError,
        });
      },
    });
  };

  const handleRemoveScan = () => {
    removeScanHandler({
      onSuccess: () => {
        setCurrentMenu(null);
        toast({
          props: {
            message: t('customize.newProject.removeScanSuccess'),
          },
        });
      },
      onError: () => {
        setCurrentMenu(null);
        showError({
          error: removeScanResult.error as Error,
          formatError: message =>
            t('projects.confirmation.removeScan.error', { message }),
        });
      },
    });
  };

  const options = useMemo(() => {
    const availableOptions: MenuOption[] = [
      {
        title: t('projects.delete'),
        onClick: () => setStep(Actions.DELETE_CONFIRMATION),
        color: 'danger600',
        icon: iconTrash,
      },
      {
        title: t('projects.editDetails'),
        onClick: () => setCurrentMenu(HomeownerAppMenuNames.EDIT_PROJECT),
        icon: iconEdit,
      },
    ];

    if (data?.project?.jobs?.length) {
      availableOptions.push({
        title: t('projects.removeScan'),
        onClick: () => setStep(Actions.REMOVE_SCAN),
        icon: iMinusCircle,
      });

      // if theres multiple scans available
      if (attachableScans.length > 1)
        availableOptions.push({
          title: t('projects.selectNewScan'),
          onClick: () => setStep(Actions.SELECT_NEW_SCAN),
          icon: iconScan,
        });
    }

    // Project photos should be displayed in the last position
    if (isMobile) {
      availableOptions.push({
        title: t('projects.viewAllProjectPhotos'),
        onClick: () => setCurrentMenu(HomeownerAppMenuNames.SAVED_IMAGES),
        icon: iconViewGridMedium,
      });
    }

    return availableOptions;
  }, [
    attachableScans.length,
    data?.project?.jobs?.length,
    isMobile,
    setCurrentMenu,
    t,
  ]);

  switch (step) {
    case Actions.MORE_ACTIONS:
      return (
        <MoreActionMenu
          onCancel={() => setCurrentMenu(null)}
          options={options}
        />
      );
    case Actions.DELETE_CONFIRMATION:
      return (
        <DisplayDestructiveConfirmation
          action={step}
          deleteAction={handleDeleteClick}
        />
      );
    case Actions.REMOVE_SCAN:
      return (
        <DisplayDestructiveConfirmation
          action={step}
          deleteAction={handleRemoveScan}
        />
      );
    case Actions.SELECT_NEW_SCAN:
      return attachedScan ? (
        <UpdateScanMenu
          attachedScan={attachedScan}
          closeMenu={() => setCurrentMenu(null)}
          projectId={projectId}
        />
      ) : null;
    default:
      return (
        <MoreActionMenu
          onCancel={() => setCurrentMenu(null)}
          options={options}
        />
      );
  }
};

export { ProjectMoreActions };
